import { useFileUploadDB, type UploadRecord, type UploadState } from '@/data/Uploads/fileUploadDb'
import * as opfs from '@/data/Uploads/opfs'
import { invariant } from '@/shared/utils/typeAssertions'
import { computed } from 'vue'

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

export function useFileUploadPersistence() {
  const fileDb = useFileUploadDB()

  /**
   * OPFS is supported in Safari, but not completely.
   * This could possibly be implemented through a WebWorker instead for full support.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/FileSystemFileHandle/createWritable#browser_compatibility
   */
  function isSupported() {
    return !isSafari
  }

  async function storeNewUpload(
    specifier: {
      workspaceId: string
      projectId: string
      propertyId: string
      parentEntityId: string | null
      entityId: string
    },
    file: File,
  ): Promise<string> {
    if (!isSupported()) return Promise.resolve('')

    const opfsPath = await opfs.storeFile(file)

    const dbRecordId = await fileDb.put({
      ...specifier,
      createdAt: Date.now(),
      opfsPath,
      originalPath: file.name,
      status: 'ready',
      updatedAt: Date.now(),
    })
    invariant(dbRecordId, 'dbRecordId is required')
    return dbRecordId
  }

  async function storeNewPendingUpload(
    specifier: {
      workspaceId: string
      projectId: string
      propertyId: string
      parentEntityId: string | null
    },
    file: File,
  ): Promise<string> {
    if (!isSupported()) return Promise.resolve('')

    const opfsPath = await opfs.storeFile(file)

    return await fileDb.put({
      ...specifier,
      createdAt: Date.now(),
      opfsPath,
      originalPath: file.name,
      status: 'idle',
      updatedAt: Date.now(),
    })
  }

  async function updateRecordStatus(dbRecordId: string, status: UploadState) {
    if (!isSupported()) return
    await fileDb.update(dbRecordId, { status })
  }

  async function updateRecordWithUrl(dbRecordId: string, url: string) {
    if (!isSupported()) return
    await fileDb.update(dbRecordId, { status: 'uploading', url })
  }

  async function updateRecordProgress(dbRecordId: string, progress: number) {
    if (!isSupported()) return
    await fileDb.update(dbRecordId, { progress })
  }

  async function updateRecordEntityId(dbRecordId: string, entityId: string) {
    if (!isSupported()) return
    await fileDb.update(dbRecordId, { entityId })
  }

  async function removeRecord(dbRecordId: string) {
    if (!isSupported()) return
    try {
      const record = await fileDb.find(dbRecordId)
      if (record) {
        await opfs.deleteFile(record.opfsPath)
      }
      await fileDb.remove(dbRecordId)
    } catch (err) {
      // eslint-disable-next-line no-console -- Nothing to really do here
      console.error(`Failed to remove upload with db record id ${dbRecordId}`, err)
    }
  }

  async function getFileFromOpfs(opfsPath: string): Promise<File | null> {
    if (!isSupported()) return Promise.resolve(null)
    return opfs.getFile(opfsPath)
  }

  const incompleteUploads = computed<UploadRecord[]>(() =>
    fileDb.allRecords.value.filter((item) => {
      return item.status !== 'done' && item.status !== 'error'
    }),
  )

  /**
   * Cleans up old upload files and database records.
   *
   * Removes IndexedDB records older than 1 week and their corresponding OPFS files.
   * Also removes any orphaned OPFS files that don't have corresponding database records.
   *
   * @returns Promise that resolves when cleanup is complete
   */
  async function cleanOldFiles() {
    if (!isSupported()) return
    const now = Date.now()
    const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000
    const keepPaths = new Set<string>()
    for (const record of fileDb.allRecords.value) {
      invariant(record.id)
      if (record.createdAt < oneWeekAgo) {
        try {
          await fileDb.remove(record.id)
        } catch (error) {
          // eslint-disable-next-line no-console -- ignoring console use for cleanup error
          console.error(`§ Failed to remove expired upload record ${record.id}`, error)
        }
      } else {
        keepPaths.add(record.opfsPath)
      }
    }
    return opfs.cleanupOldFiles(keepPaths)
  }

  return {
    cleanOldFiles,
    getFileFromOpfs,
    incompleteUploads,
    isLoaded: fileDb.isLoaded,
    isSupported,
    removeRecord,
    storeNewPendingUpload,
    storeNewUpload,
    updateRecordEntityId,
    updateRecordProgress,
    updateRecordStatus,
    updateRecordWithUrl,
  }
}
